import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, LayoutDashboard, Package, ShoppingCart, LogOut, Plus, Pencil, Trash2,
  DollarSign, Users, Boxes, X, Loader2, Store, KeyRound, Upload,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = { title: "", description: "", price: "", compare_at_price: "", image: "", category: "Electronics", stock: 100, supplier: "", featured: false };
const CATEGORIES = ["Electronics", "Home", "Fashion", "Kitchen", "Beauty", "Pets", "Fitness", "School Supplies", "Collegiate", "Toys", "Sports"];
const ORDER_STATUSES = ["processing", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null); // {mode, data}
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
  };
  useEffect(loadAll, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const saveProduct = async (form) => {
    const payload = {
      ...form,
      price: parseFloat(form.price),
      compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
      stock: parseInt(form.stock) || 0,
    };
    try {
      if (modal.mode === "edit") await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success(`Product ${modal.mode === "edit" ? "updated" : "created"}!`);
      setModal(null); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted"); loadAll();
  };

  const updateOrderStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Order updated"); loadAll();
  };

  const navItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "products", label: "Products", icon: Package },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "settings", label: "Settings", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen flex bg-cream" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="w-16 md:w-64 bg-ink text-cream flex flex-col shrink-0">
        <div className="p-4 md:p-6 flex items-center gap-2 border-b border-white/10">
          <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-black tracking-tighter hidden md:block">Snagly</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl font-bold text-sm transition-colors ${tab === n.id ? "bg-brand text-white" : "hover:bg-white/10 text-white/70"}`}>
              <n.icon className="w-5 h-5 shrink-0" /> <span className="hidden md:block">{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" className="w-full flex items-center gap-3 px-3 py-3 rounded-xl font-bold text-sm text-white/70 hover:bg-white/10 transition-colors" data-testid="admin-view-store">
            <Store className="w-5 h-5 shrink-0" /> <span className="hidden md:block">View Store</span>
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl font-bold text-sm text-white/70 hover:bg-brand-pink hover:text-white transition-colors" data-testid="admin-logout">
            <LogOut className="w-5 h-5 shrink-0" /> <span className="hidden md:block">Log out</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 lg:p-10 overflow-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight capitalize">{tab}</h1>
            <p className="text-muted-foreground text-sm">Welcome back, {user?.name}</p>
          </div>
          {tab === "products" && (
            <div className="flex items-center gap-2">
              <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold bg-brand-yellow border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="bulk-import-button">
                <Upload className="w-5 h-5" /> Bulk Import
              </button>
              <button onClick={() => setModal({ mode: "create", data: EMPTY })} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="add-product-button">
                <Plus className="w-5 h-5" /> Add Product
              </button>
            </div>
          )}
        </div>

        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {[
                { label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", icon: DollarSign, color: "bg-brand-green" },
                { label: "Orders", value: stats?.orders ?? "—", icon: ShoppingCart, color: "bg-brand-yellow" },
                { label: "Products", value: stats?.products ?? "—", icon: Boxes, color: "bg-brand text-white" },
                { label: "Customers", value: stats?.customers ?? "—", icon: Users, color: "bg-brand-pink text-white" },
              ].map((c, i) => (
                <div key={i} className={`${c.color} rounded-3xl border-2 border-ink shadow-hard p-6`} data-testid={`stat-${c.label.toLowerCase()}`}>
                  <c.icon className="w-7 h-7 mb-4" />
                  <p className="text-3xl font-black">{c.value}</p>
                  <p className="text-sm font-bold uppercase tracking-wider opacity-80">{c.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-3xl border-2 border-ink shadow-hard p-6">
              <h2 className="text-xl font-black mb-4">Recent Orders</h2>
              {orders.slice(0, 5).map((o) => (
                <div key={o.id} className="flex items-center justify-between py-3 border-b border-dashed border-ink/20 last:border-0">
                  <span className="font-bold">#{o.id.slice(-8).toUpperCase()}</span>
                  <span className="text-sm text-muted-foreground">{o.email}</span>
                  <span className="font-black">${o.amount.toFixed(2)}</span>
                </div>
              ))}
              {orders.length === 0 && <p className="text-muted-foreground text-sm">No orders yet.</p>}
            </div>
          </div>
        )}

        {tab === "products" && (
          <div className="bg-white rounded-3xl border-2 border-ink shadow-hard overflow-hidden" data-testid="admin-products">
            <table className="w-full">
              <thead className="bg-ink text-cream text-left text-xs uppercase tracking-wider">
                <tr><th className="p-4">Product</th><th className="p-4 hidden sm:table-cell">Category</th><th className="p-4">Price</th><th className="p-4 hidden md:table-cell">Stock</th><th className="p-4 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-ink/10" data-testid={`admin-product-${p.id}`}>
                    <td className="p-4"><div className="flex items-center gap-3"><img src={p.image} alt={p.title} className="w-12 h-12 rounded-lg object-cover border-2 border-ink" /><span className="font-bold text-sm line-clamp-1">{p.title}</span></div></td>
                    <td className="p-4 hidden sm:table-cell text-sm">{p.category}</td>
                    <td className="p-4 font-black">${p.price.toFixed(2)}</td>
                    <td className="p-4 hidden md:table-cell text-sm">{p.stock}</td>
                    <td className="p-4"><div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal({ mode: "edit", data: { ...p, compare_at_price: p.compare_at_price || "" } })} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-ink hover:bg-brand-yellow transition-colors" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteProduct(p.id)} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-ink hover:bg-brand-pink hover:text-white transition-colors" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders">
            {orders.length === 0 && <p className="text-muted-foreground">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-3xl border-2 border-ink shadow-hard-sm p-5" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div><p className="font-black">#{o.id.slice(-8).toUpperCase()}</p><p className="text-sm text-muted-foreground">{o.email} · {o.shipping_name}</p></div>
                  <p className="text-xl font-black">${o.amount.toFixed(2)}</p>
                  <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="border-2 border-ink rounded-full px-4 py-2 font-bold text-sm bg-white" data-testid={`order-status-${o.id}`}>
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <p className="text-sm text-muted-foreground">{o.shipping_address}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {o.items.map((it, i) => <span key={i} className="text-xs bg-secondary border border-ink rounded-full px-3 py-1 font-bold">{it.quantity}× {it.title}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "settings" && <SettingsTab />}
      </main>

      {modal && <ProductModal modal={modal} onClose={() => setModal(null)} onSave={saveProduct} categories={CATEGORIES} />}
      {bulkOpen && <BulkImportModal onClose={() => setBulkOpen(false)} onDone={loadAll} />}
    </div>
  );
}

function ProductModal({ modal, onClose, onSave, categories }) {
  const [form, setForm] = useState(modal.data);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    await onSave(form); setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl border-2 border-ink shadow-hard-lg w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="flex items-center justify-between p-6 border-b-2 border-ink sticky top-0 bg-white">
          <h2 className="text-xl font-black">{modal.mode === "edit" ? "Edit Product" : "New Product"}</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-ink hover:bg-brand-pink hover:text-white" data-testid="close-modal"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <input required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Product title" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-title-input" />
          <textarea required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium resize-none" data-testid="product-description-input" />
          <input required value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="Image URL" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-image-input" />
          <div className="grid grid-cols-2 gap-4">
            <input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Price" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-price-input" />
            <input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => set("compare_at_price", e.target.value)} placeholder="Compare price" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-compare-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full border-2 border-ink rounded-xl px-4 py-3 font-medium bg-white" data-testid="product-category-input">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-stock-input" />
          </div>
          <input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Supplier (dropship source)" className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="product-supplier-input" />
          <label className="flex items-center gap-3 font-bold cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5 border-2 border-ink rounded" data-testid="product-featured-input" /> Featured on homepage
          </label>
          <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all disabled:opacity-70" data-testid="save-product-button">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (modal.mode === "edit" ? "Update Product" : "Create Product")}
          </button>
        </form>
      </div>
    </div>
  );
}


function SettingsTab() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (form.next !== form.confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated! Use it next time you log in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg" data-testid="admin-settings">
      <div className="bg-white rounded-3xl border-2 border-ink shadow-hard p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-brand-yellow border-2 border-ink flex items-center justify-center"><KeyRound className="w-5 h-5" /></div>
          <h2 className="text-xl font-black">Change Admin Password</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Pick a strong password only you know. You'll use it to log in from now on.</p>
        <form onSubmit={submit} className="space-y-4">
          <input type="password" required value={form.current} onChange={(e) => set("current", e.target.value)} placeholder="Current password"
            className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="current-password-input" />
          <input type="password" required value={form.next} onChange={(e) => set("next", e.target.value)} placeholder="New password (min 6 chars)"
            className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="new-password-input" />
          <input type="password" required value={form.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="Confirm new password"
            className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="confirm-password-input" />
          <button type="submit" disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all disabled:opacity-70" data-testid="change-password-button">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}


const CSV_TEMPLATE = `title,price,compare_at_price,image,category,stock,supplier,description
Wireless Charging Pad,19.99,39.99,https://images.unsplash.com/photo-1591290619762-aa8f9f6c3b3f?w=600&q=80,Electronics,100,TechSource,Fast 15W wireless charger for any Qi phone
Ceramic Plant Pot Set,24.99,44.99,https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=600&q=80,Home,60,GreenThumb,Set of 3 minimalist ceramic pots with drainage`;

function parseCSVLine(line) {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else {
      if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"') q = true;
      else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseProductsCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = parseCSVLine(lines[i]);
    const r = {};
    headers.forEach((h, idx) => { r[h] = vals[idx] ?? ""; });
    rows.push(r);
  }
  return rows.map((r) => ({
    title: r.title || "",
    description: r.description || "",
    image: r.image || "",
    price: parseFloat(r.price) || 0,
    compare_at_price: r.compare_at_price ? parseFloat(r.compare_at_price) : null,
    category: r.category || "General",
    stock: parseInt(r.stock) || 100,
    supplier: r.supplier || "",
    featured: String(r.featured).toLowerCase() === "true",
  })).filter((p) => p.title && p.image && p.price > 0);
}

function BulkImportModal({ onClose, onDone }) {
  const [text, setText] = useState(CSV_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const preview = parseProductsCSV(text);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(String(ev.target.result));
    reader.readAsText(file);
  };

  const submit = async () => {
    if (preview.length === 0) { toast.error("No valid rows found. Check your columns (need at least title, price, image)."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/products/bulk", { products: preview });
      toast.success(`Imported ${data.inserted} products!`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl border-2 border-ink shadow-hard-lg w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-import-modal">
        <div className="flex items-center justify-between p-6 border-b-2 border-ink sticky top-0 bg-white z-10">
          <h2 className="text-xl font-black">Bulk Import Products</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-ink hover:bg-brand-pink hover:text-white" data-testid="close-bulk-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-brand-yellow/30 border-2 border-ink rounded-2xl p-4 text-sm">
            <p className="font-bold mb-1">📋 How it works</p>
            <p className="text-muted-foreground">Paste rows as CSV (or upload a .csv file). Keep the first header row. Columns: <b>title, price, compare_at_price, image, category, stock, supplier, description</b>. Only <b>title, price, image</b> are required. A sample is pre-filled below — edit it or replace it.</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm border-2 border-ink cursor-pointer hover:bg-secondary" data-testid="bulk-file-label">
              <Upload className="w-4 h-4" /> Upload .csv
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
            </label>
            <span className="text-sm font-bold" data-testid="bulk-preview-count">{preview.length} valid product{preview.length === 1 ? "" : "s"} detected</span>
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
            className="w-full border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-mono text-xs resize-none" data-testid="bulk-csv-input" />
          <button onClick={submit} disabled={saving || preview.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all disabled:opacity-50" data-testid="bulk-submit-button">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : `Import ${preview.length} Product${preview.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
